import * as formik from 'formik';
import * as fabric from 'office-ui-fabric-react';
import { DropdownMenuItemType } from 'office-ui-fabric-react';
import { FC, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import * as api from '../../../../../api/generated';
import { ILocationItem } from '../../../../../components/custom/LocationTree';
import { IWindowComponentProps } from '../../../../../components/windows/WindowContext';
import { priorityLevel } from '../../../../../enums';
import { IServiceTerminalQueueLocation } from './StqlList';

interface IStqlEditWindowProps {
    editableSTQLId: number;
    organizationInformationId: number;
    fetchList: () => Promise<void>;
}

interface ITerminalService {
    id: number;
    serviceId: number;
    serviceName: string | null;
    terminalId: number;
    terminalName: string | null;
}

const validationSchema = Yup.object<IServiceTerminalQueueLocation>().shape({
    terminalServiceId: Yup.number().required('Šis laukas yra privalomas'),
    locationId: Yup.number().required('Šis laukas yra privalomas'),
    queueId: Yup.number().required('Šis laukas yra privalomas')
});

const StqlEditModalWindow: FC<IWindowComponentProps<IStqlEditWindowProps>> = (props) => {
    const {
        componentProps: { editableSTQLId, fetchList, organizationInformationId },
        onClose
    } = props;

    const [serviceTerminalQueueLocation, setServiceTerminalQueueLocation] = useState<IServiceTerminalQueueLocation | null>(null);
    const [locations, setLocations] = useState<ILocationItem[] | null>(null);
    const [terminalServices, setTerminalServices] = useState<ITerminalService[] | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        const fetchForm = async () => {
            const [stqlResp, locationsResp, terminalServicesResp] = await Promise.all([
                api.servicesTerminalsQueuesLocations.getServiceTerminalQueueLocation(editableSTQLId),
                api.administration.getLocations(null),
                api.terminalServices.getAllTerminalServices(organizationInformationId)
            ]);

            setLocations(locationsResp);
            setServiceTerminalQueueLocation(stqlResp);
            setTerminalServices(terminalServicesResp);
        };
        fetchForm();
    }, []);

    const handleSubmit = async (values: IServiceTerminalQueueLocation, formikActions: formik.FormikHelpers<IServiceTerminalQueueLocation>) => {
        setIsLoading(true);
        formikActions.setStatus();
        try {
            await api.servicesTerminalsQueuesLocations.updateServiceTerminalQueueLocation(values);
            toast.success('Eilė informacija sėkmingai išsaugota');
        } catch (ex) {
            formikActions.setStatus(ex);
            toast.error(ex.error[0]);
        } finally {
            setIsLoading(false);
            formikActions.setSubmitting(false);
            formikActions.resetForm();
            fetchList();
        }
    };

    const mapLocationsOptions = (allLocations: ILocationItem[]): fabric.IDropdownOption[] => {
        var options: fabric.IDropdownOption[] = [];

        allLocations.map((x) => {
            options.push({ key: x.id, text: x.name ? x.name : '' });
        });
        return options;
    };
    const mapTerminalServicesOptions = (terminalServices: ITerminalService[]): fabric.IDropdownOption[] => {
        var options: fabric.IDropdownOption[] = [];

        if (terminalServices.length > 0) {
            var currentTerminalId: number = terminalServices[0].terminalId;
            options.push({
                key: `terminal${terminalServices[0].terminalId}`,
                text: `Terminalas - „${terminalServices[0].terminalName}“` ?? '',
                itemType: DropdownMenuItemType.Header
            });

            terminalServices.map((x) => {
                if (currentTerminalId == x.terminalId) {
                } else {
                    options.push({
                        key: `terminal${x.terminalId}`,
                        text: `Terminalas - „${x.terminalName}“` ?? '',
                        itemType: DropdownMenuItemType.Header
                    });
                    currentTerminalId = x.terminalId;
                }
                options.push({ key: x.id, text: x.serviceName ? `${x.serviceName}` : '' });
            });
        }
        return options;
    };

    return (
        <div>
            {serviceTerminalQueueLocation && locations && terminalServices && (
                <formik.Formik validationSchema={validationSchema} initialValues={{ ...serviceTerminalQueueLocation }} onSubmit={handleSubmit}>
                    {(formikProps) => (
                        <formik.Form>
                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                    <formik.Field
                                        name="terminalServiceId"
                                        render={({ field, form }: formik.FieldProps<number>) => (
                                            <fabric.Dropdown
                                                {...field}
                                                options={mapTerminalServicesOptions(terminalServices)}
                                                label="Terminalo paslaugos"
                                                onChange={({}, value: any) => form.setFieldValue(field.name, value.key)}
                                                defaultSelectedKey={field.value}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                    <formik.Field
                                        name="locationId"
                                        render={({ field, form }: formik.FieldProps<number>) => (
                                            <fabric.Dropdown
                                                {...field}
                                                options={mapLocationsOptions(locations)}
                                                label="Lokacija"
                                                onChange={({}, value: any) => form.setFieldValue(field.name, value.key)}
                                                defaultSelectedKey={field.value}
                                            />
                                        )}
                                    />
                                </div>
                            </div>

                            <div className="ms-Grid-row">
                                <div className="ms-Grid-col ms-sm6 ms-md6 ms-lg6">
                                    <formik.Field
                                        name="priorityLevel"
                                        render={({ field, form }: formik.FieldProps<number>) => (
                                            <fabric.Dropdown
                                                {...field}
                                                options={priorityLevel.displayTypesArray()}
                                                label="Prioriteto lygis"
                                                onChange={({}, value: any) => form.setFieldValue(field.name, value.key)}
                                                defaultSelectedKey={field.value}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div style={{ textAlign: 'center', marginTop: '30px' }}>
                                <fabric.PrimaryButton
                                    className="ms-slideRightIn40 ms-textAlignRight"
                                    text="Išsaugoti"
                                    disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}
                                    type="submit"
                                />
                            </div>
                        </formik.Form>
                    )}
                </formik.Formik>
            )}
        </div>
    );
};

export default StqlEditModalWindow;
